import React, { useEffect, useState } from "react";
import { onAuthChange } from "./lib/auth";
import { subscribeToActiveListings, subscribeToMyListings, subscribeToSoldListings } from "./lib/listings";
import { subscribeToActivePlayerListings, subscribeToMyPlayerListings } from "./lib/playerListings";
import { subscribeToActiveRepairListings, subscribeToMyRepairListings } from "./lib/repairListings";
import { addCartLine, createCart, fetchCart, removeCartLine, updateCartLine } from "./lib/shopify";
import { subscribeToTapes } from "./lib/tapes";
import { getTier, isSupremeActive } from "./lib/subscriptionTiers";
import { subscribeToProfile } from "./lib/users";
import { subscribeToWishlist } from "./lib/wishlist";
import { ForgotPasswordScreen, LoginScreen, RegisterScreen } from "./screens/AuthScreens";
import CartScreen from "./screens/CartScreen";
import CollectionScreen from "./screens/CollectionScreen";
import CreateListingScreen from "./screens/CreateListingScreen";
import CreatePlayerListingScreen from "./screens/CreatePlayerListingScreen";
import CreateRepairListingScreen from "./screens/CreateRepairListingScreen";
import HelpScreen from "./screens/HelpScreen";
import HomeScreen from "./screens/HomeScreen";
import ListingDetailScreen from "./screens/ListingDetailScreen";
import LimitNoticeCard from "./screens/LimitNoticeCard";
import MarketplaceScreen from "./screens/MarketplaceScreen";
import MerchScreen from "./screens/MerchScreen";
import PlayerListingDetailScreen from "./screens/PlayerListingDetailScreen";
import PlayerMarketplaceScreen from "./screens/PlayerMarketplaceScreen";
import PrivacyScreen from "./screens/PrivacyScreen";
import ProductDetailScreen from "./screens/ProductDetailScreen";
import ProfileScreen from "./screens/ProfileScreen";
import RepairDirectoryScreen from "./screens/RepairDirectoryScreen";
import RepairListingDetailScreen from "./screens/RepairListingDetailScreen";
import SplashScreen from "./screens/SplashScreen";
import TapeDetailScreen from "./screens/TapeDetailScreen";
import TapeFormScreen from "./screens/TapeFormScreen";
import WishlistScreen from "./screens/WishlistScreen";
import { T } from "./theme";
import { NavIcon, TapeIcon } from "./ui";

const PAGE_TITLE = { home: "8Track Vault", collection: "My Collection", shop: "Shop", wishlist: "Wishlist", profile: "Profile" };
const CART_ID_KEY = "8trackVaultCartId";
const SHOP_SUB_TABS = [["tapes", "Tapes"], ["merch", "Merch"], ["players", "Players"], ["repair", "Repair"]];

export default function App() {
  const [splashDone, setSplashDone] = useState(false);
  const [authScreen, setAuthScreen] = useState("login"); // login | register | forgot
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);

  const [profile, setProfile] = useState(null);
  const [tapes, setTapes] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [activeListings, setActiveListings] = useState([]);
  const [soldListings, setSoldListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [activePlayerListings, setActivePlayerListings] = useState([]);
  const [myPlayerListings, setMyPlayerListings] = useState([]);
  const [activeRepairListings, setActiveRepairListings] = useState([]);
  const [myRepairListings, setMyRepairListings] = useState([]);

  const [tab, setTab] = useState("home");
  const [shopSubTab, setShopSubTab] = useState("tapes"); // tapes | merch | players | repair
  const [detailTapeId, setDetailTapeId] = useState(null); // id of tape being viewed
  const [formMode, setFormMode] = useState(null); // null | "add" | id of tape being edited
  const [listingFormTapeId, setListingFormTapeId] = useState(null); // id of tape being listed
  const [detailListingId, setDetailListingId] = useState(null); // id of listing being viewed
  const [showingPlayerForm, setShowingPlayerForm] = useState(false);
  const [detailPlayerListingId, setDetailPlayerListingId] = useState(null);
  const [showingRepairForm, setShowingRepairForm] = useState(false);
  const [detailRepairListingId, setDetailRepairListingId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null); // Shopify product being viewed
  const [showingCart, setShowingCart] = useState(false);
  const [showingPrivacy, setShowingPrivacy] = useState(false);
  const [showingHelp, setShowingHelp] = useState(false);

  const [cartId, setCartId] = useState(() => localStorage.getItem(CART_ID_KEY));
  const [cart, setCart] = useState(null);
  const [limitNotice, setLimitNotice] = useState(null);

  useEffect(() => onAuthChange((u) => { setUser(u); setAuthLoading(false); }), []);

  useEffect(() => {
    if (!user) {
      setProfile(null); setTapes([]); setWishlist([]); setActiveListings([]); setSoldListings([]); setMyListings([]);
      setActivePlayerListings([]); setMyPlayerListings([]); setActiveRepairListings([]); setMyRepairListings([]);
      return;
    }
    const unsubProfile = subscribeToProfile(user.uid, setProfile);
    const unsubTapes = subscribeToTapes(user.uid, setTapes);
    const unsubWishlist = subscribeToWishlist(user.uid, setWishlist);
    const unsubActiveListings = subscribeToActiveListings(setActiveListings);
    const unsubSoldListings = subscribeToSoldListings(setSoldListings);
    const unsubMyListings = subscribeToMyListings(user.uid, setMyListings);
    const unsubActivePlayerListings = subscribeToActivePlayerListings(setActivePlayerListings);
    const unsubMyPlayerListings = subscribeToMyPlayerListings(user.uid, setMyPlayerListings);
    const unsubActiveRepairListings = subscribeToActiveRepairListings(setActiveRepairListings);
    const unsubMyRepairListings = subscribeToMyRepairListings(user.uid, setMyRepairListings);
    return () => {
      unsubProfile(); unsubTapes(); unsubWishlist(); unsubActiveListings(); unsubSoldListings(); unsubMyListings();
      unsubActivePlayerListings(); unsubMyPlayerListings(); unsubActiveRepairListings(); unsubMyRepairListings();
    };
  }, [user]);

  useEffect(() => {
    if (!cartId) return;
    fetchCart(cartId)
      .then((c) => { if (!c) { localStorage.removeItem(CART_ID_KEY); setCartId(null); } setCart(c); })
      .catch(() => { localStorage.removeItem(CART_ID_KEY); setCartId(null); setCart(null); });
  }, [cartId]);

  const addToCart = async (variantId, quantity) => {
    if (!cartId) {
      const c = await createCart(variantId, quantity);
      localStorage.setItem(CART_ID_KEY, c.id);
      setCartId(c.id);
      setCart(c);
    } else {
      const c = await addCartLine(cartId, variantId, quantity);
      setCart(c);
    }
  };
  const updateCartQuantity = async (lineId, quantity) => setCart(await updateCartLine(cartId, lineId, quantity));
  const removeFromCart = async (lineId) => setCart(await removeCartLine(cartId, lineId));

  if (!splashDone) return <SplashScreen onDone={() => setSplashDone(true)} />;

  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: T.font, color: T.muted }}>
        Loading…
      </div>
    );
  }

  if (!user) {
    if (authScreen === "register") return <RegisterScreen onSwitch={() => setAuthScreen("login")} />;
    if (authScreen === "forgot") return <ForgotPasswordScreen onSwitch={() => setAuthScreen("login")} />;
    return <LoginScreen onSwitch={() => setAuthScreen("register")} onForgot={() => setAuthScreen("forgot")} />;
  }

  const NAV = [["home", "Home"], ["collection", "Collection"], ["add", "Add"], ["shop", "Shop"], ["wishlist", "Wishlist"], ["profile", "Profile"]];

  const tier = getTier(profile?.subscriptionTier);
  const supremeActive = isSupremeActive(profile);
  const activeMyListingsCount = myListings.filter((l) => l.status === "active").length;

  const detailTape = detailTapeId ? tapes.find((t) => t.id === detailTapeId) : null;
  const formTape = formMode && formMode !== "add" ? tapes.find((t) => t.id === formMode) : null;
  const listingFormTape = listingFormTapeId ? tapes.find((t) => t.id === listingFormTapeId) : null;
  const detailListing = detailListingId
    ? activeListings.find((l) => l.id === detailListingId) || soldListings.find((l) => l.id === detailListingId) || myListings.find((l) => l.id === detailListingId)
    : null;
  const detailListingIsMine = !!(detailListing && detailListing.sellerUserId === user.uid);

  const detailPlayerListing = detailPlayerListingId
    ? activePlayerListings.find((l) => l.id === detailPlayerListingId) || myPlayerListings.find((l) => l.id === detailPlayerListingId)
    : null;
  const detailPlayerListingIsMine = !!(detailPlayerListing && detailPlayerListing.sellerUserId === user.uid);

  const detailRepairListing = detailRepairListingId
    ? activeRepairListings.find((l) => l.id === detailRepairListingId) || myRepairListings.find((l) => l.id === detailRepairListingId)
    : null;
  const detailRepairListingIsMine = !!(detailRepairListing && detailRepairListing.providerUserId === user.uid);

  const openTape = (t) => setDetailTapeId(t.id);
  const closeTape = () => setDetailTapeId(null);
  const openAdd = () => {
    if (tapes.length >= tier.tapeLimit) {
      setLimitNotice(`You've reached the ${tier.name} limit of ${tier.tapeLimit} tapes. Upgrade to add more.`);
      return;
    }
    setFormMode("add");
  };
  const openEdit = (t) => { setDetailTapeId(null); setFormMode(t.id); };
  const closeForm = () => setFormMode(null);
  const openListingForm = (t) => {
    if (activeMyListingsCount >= tier.listingLimit) {
      setLimitNotice(`You've reached the ${tier.name} limit of ${tier.listingLimit} active listings. Upgrade to list more.`);
      return;
    }
    setDetailTapeId(null);
    setListingFormTapeId(t.id);
  };
  const closeListingForm = () => setListingFormTapeId(null);
  const openListing = (l) => setDetailListingId(l.id);
  const closeListing = () => setDetailListingId(null);

  const requireSupreme = (action) => {
    if (!supremeActive) {
      setLimitNotice("Vault Supreme is required to post here. Upgrade from your Profile.");
      return;
    }
    action();
  };
  const openPlayerForm = () => requireSupreme(() => setShowingPlayerForm(true));
  const closePlayerForm = () => setShowingPlayerForm(false);
  const openPlayerListing = (l) => setDetailPlayerListingId(l.id);
  const closePlayerListing = () => setDetailPlayerListingId(null);

  const openRepairForm = () => requireSupreme(() => setShowingRepairForm(true));
  const closeRepairForm = () => setShowingRepairForm(false);
  const openRepairListing = (l) => setDetailRepairListingId(l.id);
  const closeRepairListing = () => setDetailRepairListingId(null);

  const openProduct = (p) => setSelectedProduct(p);
  const closeProduct = () => setSelectedProduct(null);
  const openCart = () => setShowingCart(true);
  const closeCart = () => setShowingCart(false);
  const openPrivacy = () => setShowingPrivacy(true);
  const closePrivacy = () => setShowingPrivacy(false);
  const openHelp = () => setShowingHelp(true);
  const closeHelp = () => setShowingHelp(false);

  const showingForm = formMode !== null;
  const showingDetail = !!detailTape;
  const showingListingForm = !!listingFormTape;
  const showingListingDetail = !!detailListing;
  const showingPlayerDetail = !!detailPlayerListing;
  const showingRepairDetail = !!detailRepairListing;
  const showingProductDetail = !!selectedProduct;
  const showNav = !showingForm && !showingDetail && !showingListingForm && !showingListingDetail
    && !showingPlayerForm && !showingPlayerDetail && !showingRepairForm && !showingRepairDetail
    && !showingProductDetail && !showingCart && !showingPrivacy && !showingHelp;

  let headerTitle = PAGE_TITLE[tab] || "";
  let onHeaderBack = null;
  if (showingForm) { headerTitle = formMode === "add" ? "Add New Tape" : "Edit Tape"; onHeaderBack = closeForm; }
  else if (showingDetail) { headerTitle = "Tape Detail"; onHeaderBack = closeTape; }
  else if (showingListingForm) { headerTitle = "List for Sale / Trade"; onHeaderBack = closeListingForm; }
  else if (showingListingDetail) { headerTitle = "Listing Detail"; onHeaderBack = closeListing; }
  else if (showingPlayerForm) { headerTitle = "List a Player"; onHeaderBack = closePlayerForm; }
  else if (showingPlayerDetail) { headerTitle = "Player Listing"; onHeaderBack = closePlayerListing; }
  else if (showingRepairForm) { headerTitle = "Become a Repair Provider"; onHeaderBack = closeRepairForm; }
  else if (showingRepairDetail) { headerTitle = "Repair Provider"; onHeaderBack = closeRepairListing; }
  else if (showingProductDetail) { headerTitle = "Merch"; onHeaderBack = closeProduct; }
  else if (showingCart) { headerTitle = "Your Cart"; onHeaderBack = closeCart; }
  else if (showingPrivacy) { headerTitle = "Privacy & Data"; onHeaderBack = closePrivacy; }
  else if (showingHelp) { headerTitle = "Help & Support"; onHeaderBack = closeHelp; }

  const showBackHeader = !showNav;

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: T.font, maxWidth: 430, margin: "0 auto", position: "relative", color: T.text }}>
      {!showBackHeader && tab === "home" && (
        <div style={{ background: T.bg, borderBottom: `1px solid ${T.border}`, padding: "18px 16px 20px", display: "flex", alignItems: "center", gap: 14, position: "sticky", top: 0, zIndex: 40, boxShadow: "0 1px 8px rgba(59,42,26,0.06)" }}>
          <TapeIcon size={35} />
          <div>
            <div style={{ fontSize: 30, fontWeight: "bold", color: T.text, letterSpacing: "0.03em", lineHeight: 1 }}>8Track Vault</div>
            <div style={{ fontSize: 11, color: T.accentAlt, letterSpacing: "0.26em", textTransform: "uppercase", marginTop: 5, fontWeight: "bold" }}>Preserve the Sound.</div>
          </div>
        </div>
      )}

      {!showBackHeader && tab !== "home" && (
        <div style={{ background: T.bg, borderBottom: `1px solid ${T.border}`, padding: "16px 16px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 40, boxShadow: "0 1px 8px rgba(59,42,26,0.06)" }}>
          <div>
            <div style={{ fontSize: 32, fontWeight: "bold", color: T.text, letterSpacing: "0.02em" }}>{headerTitle}</div>
          </div>
        </div>
      )}

      {showBackHeader && (
        <div style={{ background: T.bg, borderBottom: `1px solid ${T.border}`, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 40 }}>
          <button onClick={onHeaderBack} style={{ background: "none", border: "none", color: T.muted, cursor: "pointer", fontSize: 32, lineHeight: 1, padding: 0 }}>←</button>
          <div style={{ fontSize: 32, fontWeight: "bold", color: T.text }}>{headerTitle}</div>
        </div>
      )}

      <div style={{ padding: "14px 14px 110px", minHeight: "calc(100vh - 60px)", overflowY: "auto" }}>
        {showingForm && (
          <TapeFormScreen
            ownerUserId={user.uid}
            tape={formMode === "add" ? null : formTape}
            tier={tier}
            onDone={closeForm}
            onCancel={closeForm}
            onLimitReached={setLimitNotice}
          />
        )}
        {!showingForm && showingDetail && (
          <TapeDetailScreen tape={detailTape} userId={user.uid} onBack={closeTape} onEdit={openEdit} onListForSale={openListingForm} />
        )}
        {!showingForm && !showingDetail && showingListingForm && (
          <CreateListingScreen ownerUserId={user.uid} sellerDisplayName={profile?.displayName} tape={listingFormTape} onDone={closeListingForm} onCancel={closeListingForm} />
        )}
        {!showingForm && !showingDetail && !showingListingForm && showingListingDetail && (
          <ListingDetailScreen listing={detailListing} isMine={detailListingIsMine} viewerUserId={user.uid} onBack={closeListing} />
        )}
        {showingPlayerForm && (
          <CreatePlayerListingScreen ownerUserId={user.uid} onDone={closePlayerForm} onCancel={closePlayerForm} />
        )}
        {!showingPlayerForm && showingPlayerDetail && (
          <PlayerListingDetailScreen listing={detailPlayerListing} isMine={detailPlayerListingIsMine} onBack={closePlayerListing} />
        )}
        {showingRepairForm && (
          <CreateRepairListingScreen ownerUserId={user.uid} onDone={closeRepairForm} onCancel={closeRepairForm} />
        )}
        {!showingRepairForm && showingRepairDetail && (
          <RepairListingDetailScreen listing={detailRepairListing} isMine={detailRepairListingIsMine} onBack={closeRepairListing} />
        )}
        {showingProductDetail && (
          <ProductDetailScreen product={selectedProduct} onAddToCart={addToCart} onBack={closeProduct} />
        )}
        {showingCart && !showingProductDetail && (
          <CartScreen cart={cart} onUpdateQuantity={updateCartQuantity} onRemove={removeFromCart} onBack={closeCart} />
        )}
        {showingPrivacy && (
          <PrivacyScreen onBack={closePrivacy} />
        )}
        {showingHelp && !showingPrivacy && (
          <HelpScreen onBack={closeHelp} />
        )}
        {!showBackHeader && tab === "home" && (
          <HomeScreen displayName={profile?.displayName} tapes={tapes} wishlist={wishlist} onViewCollection={() => setTab("collection")} onAddTape={openAdd} />
        )}
        {!showBackHeader && tab === "collection" && (
          <CollectionScreen tapes={tapes} onSelect={openTape} onAdd={openAdd} />
        )}
        {!showBackHeader && tab === "shop" && (
          <div>
            <div style={{ display: "flex", background: T.statBg, borderRadius: 10, padding: 4, marginBottom: 14, flexWrap: "wrap", gap: 4 }}>
              {SHOP_SUB_TABS.map(([key, label]) => (
                <button key={key} onClick={() => setShopSubTab(key)} style={{ flex: "1 0 45%", background: shopSubTab === key ? T.accent : "transparent", border: "none", color: shopSubTab === key ? "#fff" : T.muted, padding: "8px 0", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: "bold", fontFamily: T.font, letterSpacing: "0.08em" }}>{label}</button>
              ))}
            </div>
            {shopSubTab === "tapes" && (
              <MarketplaceScreen activeListings={activeListings} soldListings={soldListings} myListings={myListings} onSelect={openListing} onSelectMine={openListing} />
            )}
            {shopSubTab === "merch" && (
              <MerchScreen cartQuantity={cart?.totalQuantity || 0} onSelectProduct={openProduct} onOpenCart={openCart} />
            )}
            {shopSubTab === "players" && (
              <PlayerMarketplaceScreen activeListings={activePlayerListings} myListings={myPlayerListings} onSelect={openPlayerListing} onSelectMine={openPlayerListing} onAdd={openPlayerForm} canPost={supremeActive} />
            )}
            {shopSubTab === "repair" && (
              <RepairDirectoryScreen activeListings={activeRepairListings} myListings={myRepairListings} onSelect={openRepairListing} onSelectMine={openRepairListing} onAdd={openRepairForm} canPost={supremeActive} />
            )}
          </div>
        )}
        {!showBackHeader && tab === "wishlist" && (
          <WishlistScreen ownerUserId={user.uid} wishlist={wishlist} activeListings={activeListings} tier={tier} onViewListing={openListing} />
        )}
        {!showBackHeader && tab === "profile" && (
          <ProfileScreen profile={profile} tapes={tapes} wishlist={wishlist} onOpenPrivacy={openPrivacy} onOpenHelp={openHelp} />
        )}
      </div>

      {showNav && (
        <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 430, background: T.nav, borderTop: `1px solid ${T.navBorder}`, display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", padding: "12px 2px 20px", zIndex: 50, boxSizing: "border-box", overflow: "hidden" }}>
          {NAV.map(([key, label]) => (
            <button
              key={key}
              onClick={() => (key === "add" ? openAdd() : setTab(key))}
              style={{
                background: "none", border: "none", color: key === "add" ? T.accent : (tab === key ? T.accentAlt : T.muted), cursor: "pointer",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "4px 2px",
                transition: "color 0.2s", width: "100%", minWidth: 0, overflow: "hidden",
              }}
            >
              <NavIcon name={key} size={key === "add" ? 30 : 24} />
              <span style={{ fontSize: 10, letterSpacing: "0.03em", textTransform: "uppercase", fontFamily: T.font, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{label}</span>
            </button>
          ))}
        </div>
      )}

      {limitNotice && (
        <LimitNoticeCard
          message={limitNotice}
          onClose={() => setLimitNotice(null)}
          onUpgrade={() => { setLimitNotice(null); setTab("profile"); }}
        />
      )}
    </div>
  );
}
